<script lang="ts">

  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
    } from "$lib/components/ui/dialog";

  import { Button } from "$lib/components/ui/button";
  import { Input } from "$lib/components/ui/input/index.js";
  import { Label } from "$lib/components/ui/label/index.js";

  import { type PhilatelicItem } from "@briefmarken_katalog/types";
  
  import { overlay, ui } from "$lib/stores/ui"
  import { doLogin } from "$lib/stores/auth"
  import { setItems } from "$lib/services/philatelic_items";
  import { triggerListUpdate, isImporting } from "$lib/stores/trigger_list_update"
  import { parseJSONtoPhilatelicItems } from "$lib/utils/utils"
  import { t } from "$lib/i18n";

  const importOpen = overlay('import');

  const doLoginExt = async () => {
    ui.close("import");
    return await doLogin();
  }

  let selectedFile = $state<File | null>(null);
  let numberOfEntries = $state<string | null>(null);
  let items : PhilatelicItem[] | null = null;
  let importing = $state(false);
  let importDone = $state(false);
  let importProgress = $state<{ done: number; total: number } | null>(null);

  async function setPhilatelicItems()
  {
    if (items)
    {
      importing = true;
      importProgress = null;
      isImporting.set(true);
      try {
        await setItems(items, doLoginExt, (done, total) => {
          importProgress = { done, total };
        });
        importDone = true;
      }
      catch (e: any)
      {
        alert($t('errors.importItems') + e.message);
      }
      finally {
        importing = false;
        importProgress = null;
        isImporting.set(false);
      }
    }
  }

  function closeAfterImport() {
    triggerListUpdate();
    ui.close('import');
  }

  $effect(() => {
    if (!$importOpen) {
      importDone = false;
      selectedFile = null;
      numberOfEntries = null;
      items = null;
    }
  });

  $effect(()=>{
        (async () => {
          if (selectedFile == null)
          {
            numberOfEntries = null;
          }
          else
          { 
            const reader = new FileReader();
            reader.onload = async function(e: any) {
              const fileContent = e.target.result; // The content of the file as text
              items = parseJSONtoPhilatelicItems(fileContent);
              numberOfEntries = `${items.length}`;
            };
            
            reader.onerror = function(e) {
              console.error("Error reading file:", e);
            };
            
            reader.readAsText(selectedFile); // Read the file as text
          }
        })();
  });

  function handleFileInputChange(event: Event)
  {
    const target = event.target as HTMLInputElement;
    if (target.files)
    {
      selectedFile = target.files[0];
    }
  }

</script>

<Dialog bind:open={$importOpen}>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>{$t('import.title')}</DialogTitle>
      <DialogDescription>
        {$t('import.description').split('\n')[0]}<br>
        {$t('import.description').split('\n')[1]}
      </DialogDescription>
    </DialogHeader>
      <div class="grid grid-cols-2 items-center gap-4">
        <Label for="file">{$t('import.dataFile')}</Label>
        <Input id="file" type="file" accept=".json" onchange={ handleFileInputChange } />
      </div>
      <div class="grid grid-cols-2 items-center gap-4">
        <Label for="file">{$t('import.numberOfEntries')}</Label>
        <span>{numberOfEntries ?? $t('import.notAvailable')}</span>
      </div>

    {#if importing}
      <div class="flex flex-col gap-1">
        <div class="flex items-center justify-between text-sm text-muted-foreground">
          <span>{$t('import.importing')}</span>
          {#if importProgress}
            <span>{importProgress.done} / {importProgress.total}</span>
          {/if}
        </div>
        <div class="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            class="bg-primary h-2 rounded-full transition-all duration-150"
            style="width: {importProgress ? Math.round((importProgress.done / importProgress.total) * 100) : 0}%"
          ></div>
        </div>
      </div>
    {/if}

    <DialogFooter>
      {#if importDone}
        <p class="text-sm text-muted-foreground mr-auto self-center">{$t('import.done')}</p>
        <Button onclick={closeAfterImport}>{$t('import.close')}</Button>
      {:else}
        <Button disabled={selectedFile === null || importing} onclick={setPhilatelicItems}>
          {$t('import.button')}
        </Button>
      {/if}
    </DialogFooter>
  </DialogContent>
</Dialog>